import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { translations } from '../translations/zh';

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: "easeOut"
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.15,
      ease: "easeInOut"
    }
  }
};



const Features = () => {
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const handleClose = () => {
    setSelectedId(null);
  };

  return (
    <motion.section className="py-20 bg-white/50">
      <div className="container mx-auto px-8">
        <motion.h2 
          className="text-4xl font-bold text-center text-[#3D587D] mb-16"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
        >
          {translations.features.title}
        </motion.h2>
        <div className="grid md:grid-cols-2 gap-12">
          {translations.features.items.map((feature, index) => (
            <motion.div
              key={index}
              layoutId={`feature-${index}`}
              onClick={() => setSelectedId(index)}
              className="flex gap-6 items-start p-6 bg-white rounded-xl shadow-lg cursor-pointer"
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              whileHover={{ 
                y: -5, 
                transition: { 
                  duration: 0.2,
                  ease: "easeOut"
                } 
              }}
            >
              <motion.span layout className="text-4xl">{feature.icon}</motion.span>
              <motion.div layout>
                <motion.h3 layout className="text-xl font-semibold text-[#3D587D] mb-2">{feature.title}</motion.h3>
                <motion.p layout className="text-[#407CBD]">{feature.description}</motion.p>
              </motion.div>
            </motion.div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {selectedId !== null && (
            <div className="fixed inset-0 z-50 overflow-hidden">
              <motion.div
                variants={overlayVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onClick={handleClose}
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              />
              <div className="absolute inset-0 flex items-center justify-center p-4" onClick={handleClose}>
                <motion.div
                  layoutId={`feature-${selectedId}`}
                  className="relative w-[90%] max-w-2xl bg-white rounded-xl shadow-2xl p-8"
                  onClick={(e) => e.stopPropagation()}
                  transition={{
                    layout: {
                      duration: 0.2,
                      ease: "easeOut"
                    }
                  }}
                >
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ 
                      opacity: 1, 
                      scale: 1,
                      transition: {
                        duration: 0.15,
                        delay: 0.1
                      }
                    }}
                    exit={{ 
                      opacity: 0, 
                      scale: 0.8,
                      transition: {
                        duration: 0.1
                      }
                    }}
                    onClick={handleClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                  >
                    ✕
                  </motion.button>
                  <motion.div 
                    className="flex gap-6 items-start"
                    initial={{ opacity: 0 }}
                    animate={{ 
                      opacity: 1,
                      transition: {
                        duration: 0.2,
                        delay: 0.1
                      }
                    }}
                    exit={{ 
                      opacity: 0,
                      transition: {
                        duration: 0.15
                      }
                    }}
                  >
                    <motion.span 
                      layout
                      className="text-6xl"
                    >
                      {translations.features.items[selectedId].icon}
                    </motion.span>
                    <div className="flex-1">
                      <motion.h3 
                        layout
                        className="text-2xl font-semibold text-[#3D587D] mb-4"
                      >
                        {translations.features.items[selectedId].title}
                      </motion.h3>
                      <motion.p 
                        layout
                        className="text-[#407CBD] mb-6"
                      >
                        {translations.features.items[selectedId].description}
                      </motion.p>
                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ 
                          opacity: 1, 
                          y: 0,
                          transition: {
                            duration: 0.2,
                            delay: 0.2
                          }
                        }}
                        exit={{ 
                          opacity: 0, 
                          y: 10,
                          transition: {
                            duration: 0.15
                          }
                        }}
                        className="text-gray-600 leading-relaxed"
                      >
                        {translations.features.items[selectedId].longDescription}
                      </motion.p>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </motion.section>
  );
};

export default Features;